import { get, isEqual } from 'lodash';
import {
  registerDerivedProp,
  calculateDerivedProps,
} from './derivedPropsProcessing';

import normalizeEventName from './normalizeEventName';

const initialState = {
  adType: '',
  adPlaying: false,
  adTimeToAdStart: 0,
  adLinear: null,
  adNumber: 0,
  memberIsStarting: false,
};

export default (player, onUpdate, getState) => {
  player.on('ad:external:vastData', listen);
  player.on('ad:external:memberStartedPlaying', listen);
  player.on('ad:external:vastEvent', listen);
  player.on('ad:external:adWillStartIn', listen);
  player.on('ad:external:adComplete', listen);
  player.on('property:ad:vendor:state', listen);
  player.on('property:ad:vendor:position', listen);
  player.on('property:ad:vendor:duration', listen);
  player.on('property:ad:timeLabels', listen);
  player.on('ad:vendor:imaIsLinear', listen);
  player.on('ad:external:memberIsStarting', listen);

  registerDerivedProp(
    'adCurrentTime',
    'adDuration' /* MHP-1865 */,
    (currentTime, duration) =>
      /*Math.round*/ (currentTime / duration) * 100 || 0,
    'adProgress'
  );
  registerDerivedProp(
    'adCurrentTime',
    'adSkipTime',
    (currentTime, skipTime) => currentTime < skipTime,
    'adIsSkipDisabled'
  );
  registerDerivedProp(
    'adVastData',
    'adDuration',
    'adSkipTime',
    (vastData, duration, skipTime) => skipTime < Math.floor(duration),
    'adSkipButton'
  );
  registerDerivedProp(
    'adCurrentTime',
    'adSkipTime',
    (currentTime, skipTime) => Math.ceil(skipTime - currentTime),
    'adSkipLeft'
  );

  return initialState;

  function listen(event) {
    let updatedData;
    const data = event.data;
    const name = normalizeEventName(event.name);
    const prevState = getState();
    switch (name) {
      case 'ad:external:vastData':
        {
          const { vastData } = data;
          updatedData = {
            adType: data.type,
            adVastData: vastData,
            adDuration: get(vastData, ['duration', 0], 0),
            adSkipTime: get(vastData, ['skipTime', 0], 300), // TODO: 300 === 5min, т.к. duration обычно меньше 5 мин (подробности в MHP-934)
            adSkipTime2: get(vastData, ['skipTime2', 0]),
            adUrl: get(vastData, ['clickthrough', 0], ''),
            adLinkTxt: get(vastData, ['linkTxt', 0], ''),
            adControls: selectorAdControls(vastData),
            adIsClickable: selectorAdIsClickable(vastData),
            adTitleTxt: get(vastData, ['titleTxt', 0], ''),
            adIsTitleVisible: selectorControlsVisible(vastData, 'titleVisible'),
            adProgressVisible: selectorControlsVisible(
              vastData,
              'progressVisible'
            ),
            adIsImageMediaFile: selectorIsImageMediaFile(vastData),
            adIsVPAID: get(vastData, 'mediaFile.0.apiFramework', '').includes(
              'VPAID'
            ),
            adIsImaAd:
              get(vastData, 'mediaFile.0.url', '').indexOf('.doubleclick.') !==
              -1,
            adCount: data.count,
            adNumber: data.number,
            adLinear: data.linear,
            adCurrentTime: 0,
          };
        }
        break;
      case 'ad:external:vastEvent':
        if (data.vastEvent === 'creativeView') {
          updatedData = {
            adTimeToAdStart: 0,
          };
        }
        break;
      case 'ad:external:adWillStartIn':
        updatedData = {
          adTimeToAdStart: data,
        };
        break;
      case 'ad:external:memberStartedPlaying':
        updatedData = {
          adPlaying: true,
          adTimeToAdStart: 0,
        };
        break;
      case 'ad:external:adComplete':
        updatedData = {
          ...initialState,
        };
        break;
      case 'ad:vendor:state':
        if (data === 2) {
          updatedData = { adPaused: true };
        } else if (data === 1) {
          updatedData = { adPaused: false };
        }
        break;
      case 'ad:vendor:position':
        updatedData = {
          adCurrentTime: data,
        };
        break;
      case 'ad:vendor:duration':
        updatedData = {
          adDuration: data,
        };
        break;
      case 'ad:timeLabels':
        if (!isEqual(prevState.adTimeLabels, data)) {
          updatedData = {
            adTimeLabels: data,
          };
        }
        break;
      case 'ad:vendor:imaIsLinear':
        updatedData = {
          adLinear: data,
        };
        break;
      case 'ad:external:memberIsStarting':
        updatedData = {
          memberIsStarting: true,
        };
        break;
      default:
    }

    updatedData = {
      ...updatedData,
      ...calculateDerivedProps(updatedData, getState()),
    };

    onUpdate(updatedData);
  }
};

function selectorAdControls(data) {
  let result = true;
  const controls = get(data, 'controls', []);
  try {
    for (let i = 0, l = controls.length; i < l; ++i) {
      if (controls[i] !== undefined) result *= Number(controls[i]);
    }
  } catch {
    result = true;
  }
  return Boolean(result); // convert to boolean, because result could be 0 or 1
}

function selectorAdIsClickable(data) {
  let result = get(data, ['isClickable', 0]);
  if (result === undefined) {
    result = get(data, ['clickthrough', 0]);
  }
  return Boolean(result);
}

function selectorControlsVisible(data, controlName) {
  let result = true;
  const control = get(data, controlName, []);
  try {
    for (let i = 0, l = control.length; i < l; ++i) {
      result *= Number(control[i]);
    }
  } catch {
    result = true;
  }
  return Boolean(result); // convert to boolean, because result could be 0 or 1
}

function selectorIsImageMediaFile(data) {
  let result = false;
  const mediaFileType = get(data, ['mediaFile', 0, 'type'], '');
  if (mediaFileType) {
    result =
      mediaFileType.includes('jpg') ||
      mediaFileType.includes('jpeg') ||
      mediaFileType.includes('gif') ||
      mediaFileType.includes('png') ||
      mediaFileType.includes('image');
  }
  return result;
}
